import { useQuery, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import type { ExamPackageFeature } from '@/lib/entitlement-features';
import { courseKeys } from '@/hooks/useCourses';
import { SUBSCRIPTION_STATUS_QUERY_KEY } from '@/hooks/useSubscription';

export type EntitlementState = 'none' | 'purchased' | 'scheduled' | 'active' | 'expired' | 'trial';

/**
 * Canonical React Query key prefix for the entitlement-access query.
 *
 * MUST be the first element of the `useEntitlement` queryKey AND the key
 * passed to every `invalidateQueries` call that needs to refresh entitlement
 * state (after starting a trial, completing a purchase, or activating a cycle).
 *
 * History: this used to be the literal `'bar-package-access'`. Commit 4101d98b
 * renamed the query key to `'entitlement-access'` (to support both bar_package
 * and ube_package) but left ~11 `invalidateQueries(['bar-package-access'])`
 * call sites untouched. Because React Query matches keys by prefix, those
 * invalidations silently no-op'd — so after starting a trial the cached
 * `'none'` entitlement persisted for the full 5-minute staleTime, leaving the
 * "Start free trial" banner up and the practice gates locked until a manual
 * page reload. Centralizing the key here removes the drift class entirely.
 */
export const ENTITLEMENT_ACCESS_QUERY_KEY = 'entitlement-access' as const;

/**
 * Invalidate (and refetch active) every query whose answer depends on the
 * user's entitlements. Call this after any mutation that changes one:
 * `/pay/start-trial`, `/pay/create-payment` success, `/pay/activate-cycle`.
 * Prefer this over an inline `invalidateQueries` so the keys can never drift.
 *
 * - entitlement-access: every feature + user variant (useEntitlement).
 * - courses (courseKeys.all, prefix of every useCourses/useCourse key): catalog
 *   rows carry content_locked, which the server derives from these same
 *   entitlements. With a 10-minute staleTime, a buyer who activated a cycle
 *   and went back to a lesson kept seeing the locked card (yMhzzTwwi0u0WLYNTXQH).
 * - subscriptionStatus: get_user_subscription() bundles Unlimited from an
 *   ACTIVE bar_package/ube_package row, and useCourseAccess gates standard
 *   videos on it. Without this, a fresh unlocked row still showed the
 *   "upgrade" prompt over the video for up to 5 minutes.
 * Deliberately NOT done in refreshSubscription: that runs on a 5-minute
 * interval for every signed-in user (SubscriptionContext), and would refetch
 * the course catalog on every tick.
 *
 * Only the entitlement refetch is awaited. Callers await this and then
 * navigate, and the route guards they navigate into read the entitlement.
 * The other two are marked stale synchronously and refetch in the background:
 * - Courses: awaiting would hold the navigation for a catalog refetch that the
 *   navigation then discards (code-reviewer L3, ledger X2wgW1QJDr3VDHtV1k5J).
 * - subscriptionStatus: no awaiting caller navigates to a surface gated on it.
 *   Every destination is a NextGen route gated on the entitlement, and
 *   useEffectiveJdsAccess already reads the entitlement state. The query is
 *   always mounted (SubscriptionProvider), so awaiting it would add an RPC
 *   round-trip to every trial start and activation.
 */
export function invalidateEntitlementAccess(queryClient: QueryClient): Promise<void> {
  void queryClient.invalidateQueries({ queryKey: courseKeys.all });
  void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_STATUS_QUERY_KEY] });
  return queryClient.invalidateQueries({ queryKey: [ENTITLEMENT_ACCESS_QUERY_KEY] });
}

export interface CycleInfo {
  cycleName: string;
  cycleKey: string;
  cycleStartDate: string;
  examDate: string;
}

export interface BarPackageAccess {
  hasAccess: boolean;
  /** @deprecated Use hasAccess instead */
  hasBarPackage: boolean;
  entitlementState: EntitlementState;
  cycleInfo: CycleInfo | null;
  expiresAt: string | null;
  /**
   * An expired entitlement that was a free TRIAL and was never paid for.
   * Distinguishes a lapsed trial from a lapsed PAID plan, which both read
   * entitlementState 'expired'. See isLapsedTrialRow. Optional; absent means
   * false (not known to be a lapsed trial).
   */
  lapsedTrial?: boolean;
  isLoading: boolean;
  /**
   * The query SETTLED IN ERROR. Distinct from `isLoading`, and both must be
   * checked before acting on `entitlementState`: on error the query is no
   * longer pending, so `isLoading` is false while `data` is undefined and
   * `entitlementState` falls through to its `?? 'none'` default below. Anything
   * that MUTATES on 'none' (starting a trial) would then act on a value that
   * was never an answer — `.claude/rules/silent-degradation.md`.
   */
  isError: boolean;
}

/**
 * Derive a human-readable cycle name from an exam_cycle key.
 * e.g. '2026-07' → 'July 2026', '2027-02' → 'February 2027'
 */
function deriveCycleName(examCycle: string): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const parts = examCycle.split('-');
  if (parts.length >= 2) {
    const year = parts[0];
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${months[monthIndex]} ${year}`;
    }
  }
  return examCycle;
}

/**
 * Extended row type for user_entitlements including cycle columns.
 * These columns may not yet exist in the generated types but will be
 * added by the activation migration. PostgREST will return them as
 * additional fields when present on the table.
 */
interface EntitlementRow {
  id: string;
  status: string | null;
  state: string | null;
  expires_at: string | null;
  exam_cycle?: string | null;
  cycle_start_date?: string | null;
  exam_date?: string | null;
  trial_started_at?: string | null;
  converted_from_trial?: boolean | null;
  stripe_payment_intent_id?: string | null;
  purchased_at?: string | null;
}

/**
 * Is this an expired row that began as a free trial and was NEVER paid for?
 * Exported for tests. Both a lapsed trial and a lapsed paid plan read
 * entitlementState 'expired' (the mapping above folds an expired 'trial' row
 * into 'expired'), so the state alone cannot tell them apart.
 *
 * Every marker must say "never paid", because each one alone has a hole:
 * - trial_started_at is stamped ONLY at trial creation (trial.ts INSERT) and
 *   is preserved through conversion (webhook.ts deliberately leaves it out of
 *   its SET), so a converted buyer keeps it.
 * - converted_from_trial is set by the webhook only since 2026-05-28
 *   (forward-only), so older conversions read false.
 * - stripe_payment_intent_id is set on every paid write, but grant-guarantee.ts
 *   sets it back to NULL on a PAID buyer's row.
 * - purchased_at is stamped at every sale and nothing clears it.
 * So: trial origin, not converted, no payment intent, no sale.
 * Live distribution checked 2026-09-30 (bar_package, read-only): 89 expired
 * rows match (trial origin, never paid); the 4 expired paid rows all carry a
 * payment intent and purchased_at, and none match.
 */
export function isLapsedTrialRow(
  entitlementState: EntitlementState,
  row: {
    trial_started_at?: string | null;
    converted_from_trial?: boolean | null;
    stripe_payment_intent_id?: string | null;
    purchased_at?: string | null;
  },
): boolean {
  return (
    entitlementState === 'expired' &&
    !!row.trial_started_at &&
    row.converted_from_trial !== true &&
    !row.stripe_payment_intent_id &&
    !row.purchased_at
  );
}

/**
 * Generic entitlement access hook. Checks whether the current user holds a
 * `user_entitlements` row for the given exam-package feature and derives its
 * lifecycle state, cycle info, expiration, and access flag.
 *
 * `useBarPackageAccess` and `useUbePackageAccess` are thin wrappers over this.
 *
 * @param feature exact `user_entitlements.feature` value (e.g. 'bar_package' | 'ube_package')
 */
export function useEntitlement(feature: ExamPackageFeature): BarPackageAccess {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [ENTITLEMENT_ACCESS_QUERY_KEY, feature, user?.id],
    queryFn: async () => {
      // Select base columns that exist in generated types.
      // Cycle columns (exam_cycle, cycle_start_date, exam_date) will be
      // returned by PostgREST when present on the table even though they
      // aren't in the generated TS types yet.
      // Exact-match on feature — never a prefix match (see entitlement-features.ts).
      const { data: rawData, error } = await supabase
        .from('user_entitlements')
        .select(
          'id, status, state, expires_at, exam_cycle, cycle_start_date, exam_date, ' +
            'trial_started_at, converted_from_trial, stripe_payment_intent_id, purchased_at',
        )
        .eq('user_id', user!.id)
        .eq('feature', feature)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!rawData) {
        return {
          hasAccess: false,
          entitlementState: 'none' as EntitlementState,
          cycleInfo: null,
          expiresAt: null,
        };
      }

      // Cast to our extended type to access cycle columns if they exist
      const data = rawData as unknown as EntitlementRow;

      // Map the lifecycle column to our EntitlementState type.
      // Prefer `state` (the newer lifecycle column added 2026-04-15) over legacy `status`.
      // Why: trial users are written with status='active' (CHECK-valid) + state='trial';
      // reading status would mis-classify them as 'active' and hide the trial banner.
      // Fall back to `status` for legacy rows where `state` is null.
      const lifecycleValue = (data.state ?? data.status ?? '') as string;
      let entitlementState: EntitlementState;
      if (lifecycleValue === 'active' || lifecycleValue === 'purchased' || lifecycleValue === 'scheduled' || lifecycleValue === 'expired' || lifecycleValue === 'trial') {
        entitlementState = lifecycleValue;
      } else {
        // Unknown state — treat as none
        entitlementState = 'none';
      }

      // Check expiration: if active or trial but expired, override state
      const isExpired = !!data.expires_at && new Date(data.expires_at) < new Date();
      if (isExpired && (entitlementState === 'active' || entitlementState === 'trial')) {
        entitlementState = 'expired';
      }

      const hasAccess = (entitlementState === 'active' || entitlementState === 'trial') && !isExpired;

      // Build cycle info if the user has selected an exam cycle
      let cycleInfo: CycleInfo | null = null;
      if (data.exam_cycle) {
        cycleInfo = {
          cycleName: deriveCycleName(data.exam_cycle),
          cycleKey: data.exam_cycle,
          cycleStartDate: data.cycle_start_date ?? '',
          examDate: data.exam_date ?? '',
        };
      }

      return {
        hasAccess,
        entitlementState,
        cycleInfo,
        expiresAt: data.expires_at ?? null,
        lapsedTrial: isLapsedTrialRow(entitlementState, data),
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    hasAccess: query.data?.hasAccess ?? false,
    hasBarPackage: query.data?.hasAccess ?? false,
    entitlementState: query.data?.entitlementState ?? 'none',
    cycleInfo: query.data?.cycleInfo ?? null,
    expiresAt: query.data?.expiresAt ?? null,
    lapsedTrial: query.data?.lapsedTrial ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Check the current user's bar_package (NextGen / Studicata) entitlement.
 * Thin wrapper over useEntitlement('bar_package') — preserves the existing
 * public API consumed across NextGen surfaces.
 */
export function useBarPackageAccess(): BarPackageAccess {
  return useEntitlement('bar_package');
}

/**
 * Check the current user's ube_package (standalone UBE/MBE) entitlement.
 */
export function useUbePackageAccess(): BarPackageAccess {
  return useEntitlement('ube_package');
}
